import { useEffect, useState } from 'react';

import { S3FileItem } from '../../../services/query/aiPrompt/S3BrowserQuery.services';
import {
    isPreviewableImageKey,
    loadS3FullImageSource,
    loadS3ImageSource,
    type S3ImageSourceLoadResult,
} from './SCUIS3Browser.helpers';

type ImageLoadState = {
    src: string | null;
    loading: boolean;
    error: boolean;
};

const idleState: ImageLoadState = { src: null, loading: true, error: false };

function applyImageResult(
    result: S3ImageSourceLoadResult | null,
    setState: (next: ImageLoadState) => void
): string | null {
    if (result) {
        setState({ src: result.src, loading: false, error: false });
        return result.objectUrl ?? null;
    }
    setState({ src: null, loading: false, error: true });
    return null;
}

/** Shared lazy image loader for thumbnail / preview panels (reduces S3776 in components). */
export function useS3PreviewImageSource(
    file: Pick<S3FileItem, 'key' | 'url' | 'thumbnailKey'>,
    options: { enabled?: boolean; preferThumbnail?: boolean } = {}
): ImageLoadState {
    const { enabled = true, preferThumbnail = true } = options;
    const [state, setState] = useState<ImageLoadState>(idleState);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        let objectUrl: string | null = null;
        let cancelled = false;

        setState({ src: null, loading: true, error: false });

        if (!isPreviewableImageKey(file.key)) {
            setState({ src: null, loading: false, error: false });
            return;
        }

        const loader = preferThumbnail
            ? loadS3ImageSource(file)
            : loadS3FullImageSource(file);

        loader
            .then((result) => {
                if (cancelled) return;
                objectUrl = applyImageResult(result, setState);
            })
            .catch(() => {
                if (!cancelled) setState({ src: null, loading: false, error: true });
            });

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [enabled, preferThumbnail, file.key, file.url, file.thumbnailKey]);

    return state;
}
